// LivestreamUserTriggerModals.jsx
import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import DiamondIconSrc from '../../img/ic-diamond-md.svg';

import { ModalId } from '../resource/trackingConstants.js';
import ModalView from '../container/ModalView.js';
import UserAvatar from '../container/UserAvatar.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import Animator from '../component/Animator.jsx';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { formatTimeStringBySec } from '../resource/formatTimeString.js';
import media from '../style/media.js';
import { textColor } from '../style/variables.js';

export const LivestreamUserTriggerModals = ({
  timestampOffsetSeconds = 0,
  privateStreamPrice = 0,
  exclusivePrivatePrice = 0,
  countdownToPrivateExp = 0,
  countdownToExclusivePrivateNbf = 0,
  triggerExclusiveHoldingtime = 0,
  isExclusivePrivatePreparing = false,
  streamerId = '',
  meId = '',
}) => {
  const { t } = useTranslation();
  return (
    <>
      <ModalView
        id="ModalTriggerPrivateSucceeded"
        modalId={ModalId.LiveStream.ModalTriggerPrivateSucceeded}
        render={() => (
          <UniversalComponent
            filename="Container_TriggerPrivateNotificationModal"
            id="ModalTriggerPrivateSucceeded"
            modalId={ModalId.LiveStream.ModalTriggerPrivateSucceeded}
            titleI18nKey="trigger_private_succeeded"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/TriggerPrivateNotificationModal.js')
            }
            body={
              <FlexCenterBox>
                <UserAvatarWrapper>
                  <UserAvatar id={streamerId} size={64} />
                </UserAvatarWrapper>
                <Animator
                  render={() => {
                    const now = getCurrentUnixTimestamp({
                      offsetSeconds: timestampOffsetSeconds,
                    });
                    const countdownSeconds = Math.max(
                      countdownToPrivateExp - now,
                      0
                    );
                    const remainingTime =
                      formatTimeStringBySec(countdownSeconds);
                    return (
                      <Text>
                        {t('alert_title_countdown_into_private_stream', {
                          ns: TranslationNamespace.LIVESTREAM,
                        })}{' '}
                        {remainingTime}
                      </Text>
                    );
                  }}
                  fps={1}
                />
              </FlexCenterBox>
            }
          />
        )}
      />
      <ModalView
        id="GroupPrivateDeclinedModal"
        modalId={ModalId.LiveStream.ModalTriggerPrivateFailed}
        render={() => (
          <UniversalComponent
            filename="Container_TriggerPrivateNotificationModal"
            id="GroupPrivateDeclinedModal"
            modalId={ModalId.LiveStream.ModalTriggerPrivateFailed}
            titleI18nKey="trigger_private_failed"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/TriggerPrivateNotificationModal.js')
            }
            body={
              <FlexCenterBox>
                <ReturnDiamond>
                  <ReturnDiamondText>
                    {t('trigger_private_refunds', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })}
                  </ReturnDiamondText>
                  <Diamond>
                    <img src={DiamondIconSrc} alt="" width={14} height={14} />
                    <span>{privateStreamPrice}</span>
                  </Diamond>
                </ReturnDiamond>
                <Description>
                  {t(
                    isExclusivePrivatePreparing
                      ? 'trigger_failed_description_exclusive'
                      : 'trigger_failed_description_cooldown',
                    {
                      ns: TranslationNamespace.LIVESTREAM,
                    }
                  )}
                  {t('trigger_failed_description_cooldown', {
                    ns: TranslationNamespace.LIVESTREAM,
                  })}
                </Description>
              </FlexCenterBox>
            }
          />
        )}
      />
      <ModalView
        id="ModalTriggerExclusiveSucceeded"
        modalId={ModalId.LiveStream.ModalTriggerExclusiveSucceeded}
        render={() => (
          <UniversalComponent
            filename="Container_TriggerPrivateNotificationModal"
            id="ModalTriggerExclusiveSucceeded"
            modalId={ModalId.LiveStream.ModalTriggerExclusiveSucceeded}
            titleI18nKey="trigger_exclusive_succeeded"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/TriggerPrivateNotificationModal.js')
            }
            body={
              <FlexCenterBox>
                <UserAvatarRow>
                  <UserAvatarWrapper>
                    <UserAvatar id={streamerId} size={64} />
                  </UserAvatarWrapper>
                  <UserAvatarWrapper>
                    <UserAvatar id={meId} size={64} />
                  </UserAvatarWrapper>
                </UserAvatarRow>
                <Animator
                  render={() => {
                    const now = getCurrentUnixTimestamp({
                      offsetSeconds: timestampOffsetSeconds,
                    });
                    const countdownSeconds = Math.max(
                      countdownToExclusivePrivateNbf - now,
                      0
                    );
                    const remainingTime =
                      formatTimeStringBySec(countdownSeconds);
                    return (
                      <Text>
                        {t('alert_title_countdown_into_exclusive_stream', {
                          ns: TranslationNamespace.LIVESTREAM,
                        })}{' '}
                        {remainingTime}
                      </Text>
                    );
                  }}
                  fps={1}
                />
              </FlexCenterBox>
            }
          />
        )}
      />
      <ModalView
        id="ModalTriggerExclusiveFailed"
        modalId={ModalId.LiveStream.ModalTriggerExclusiveFailed}
        render={() => (
          <UniversalComponent
            filename="Container_TriggerPrivateNotificationModal"
            id="ModalTriggerExclusiveFailed"
            modalId={ModalId.LiveStream.ModalTriggerExclusiveFailed}
            titleI18nKey="trigger_exclusive_failed"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/TriggerPrivateNotificationModal.js')
            }
            body={
              <FlexCenterBox>
                <ReturnDiamond>
                  <ReturnDiamondText>
                    {t('trigger_private_refunds', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })}
                  </ReturnDiamondText>
                  <Diamond>
                    <img src={DiamondIconSrc} alt="" width={14} height={14} />
                    <span>
                      {exclusivePrivatePrice *
                        (triggerExclusiveHoldingtime / 60)}
                    </span>
                  </Diamond>
                </ReturnDiamond>
                <Description>
                  {t(
                    isExclusivePrivatePreparing
                      ? 'trigger_failed_description_exclusive'
                      : 'trigger_failed_description_cooldown',
                    {
                      ns: TranslationNamespace.LIVESTREAM,
                    }
                  )}
                </Description>
              </FlexCenterBox>
            }
          />
        )}
      />
      <ModalView
        id="ModalTurnToExclusivePrivate"
        modalId={ModalId.LiveStream.ModalTurnToExclusivePrivate}
        render={() => (
          <UniversalComponent
            filename="Container_TriggerPrivateNotificationModal"
            id="ModalTurnToExclusivePrivate"
            modalId={ModalId.LiveStream.ModalTurnToExclusivePrivate}
            titleI18nKey="exclusive_about_to_start"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/TriggerPrivateNotificationModal.js')
            }
            body={
              <FlexCenterBox>
                <Title>
                  {t('trigger_failed_description_exclusive', {
                    ns: TranslationNamespace.LIVESTREAM,
                  })}
                </Title>
              </FlexCenterBox>
            }
          />
        )}
      />
    </>
  );
};

LivestreamUserTriggerModals.propTypes = {
  timestampOffsetSeconds: PropTypes.number,
  privateStreamPrice: PropTypes.number,
  exclusivePrivatePrice: PropTypes.number,
  countdownToPrivateExp: PropTypes.number,
  countdownToExclusivePrivateNbf: PropTypes.number,
  triggerExclusiveHoldingtime: PropTypes.number,
  isExclusivePrivatePreparing: PropTypes.bool,
  streamerId: PropTypes.string,
  meId: PropTypes.string,
};

const FlexCenterBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const UserAvatarWrapper = styled.div`
  padding: 6px;
  margin-left: -24px;
  :first-child {
    margin-left: 0;
    z-index: 1;
  }
`;

const UserAvatarRow = styled.div`
  display: flex;
`;

const FontText = css`
  color: white;
  text-align: center;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  letter-spacing: 0.016px;
  font-size: 18px;
  ${media.mobile`
  font-size: 16px;
`}
`;

const Text = styled.div`
  display: flex;
  align-items: center;
  ${FontText}
`;

const ReturnDiamond = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
`;

const ReturnDiamondText = styled.div`
  ${FontText}
`;

const Description = styled.div`
  color: ${textColor.grey};
  text-align: center;
  font-weight: 400;
  line-height: 150%; /* 18px */
  letter-spacing: 0.012px;
  font-size: 14px;
  ${media.mobile`
    font-size: 12px;
  `}
`;

const Diamond = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${textColor.primary};
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: 0.014px;
  font-size: 16px;
  ${media.mobile`
    font-size: 14px;
  `}
`;

const Title = styled.div`
  ${FontText}
`;

export default LivestreamUserTriggerModals;
